import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CARBON_TREND_DATA, SOCIAL_TREND_DATA } from './esgData';

const COLORS = { actual: '#0f4c81', target: '#94a3b8', jobs: '#0f4c81', community: '#0d9488' };

const CarbonTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload?.map((p) => (
        <p key={p?.dataKey} style={{ color: p?.color }}>
          {p?.name}: {Number(p?.value)?.toLocaleString()} tCO₂e
        </p>
      ))}
    </div>
  );
};

const SocialTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload?.map((p) => (
        <p key={p?.dataKey} style={{ color: p?.color }}>
          {p?.name}: {p?.dataKey === 'communitySpend' ? `£${p?.value}m` : Number(p?.value)?.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

export default function SustainabilityCharts() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Carbon Performance */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="font-heading font-semibold text-foreground mb-1" style={{ fontSize: '0.9375rem' }}>Carbon Performance Over Time</h3>
        <p className="font-caption text-muted-foreground text-xs mb-4">Actual CO₂ avoided vs target trajectory (tCO₂e)</p>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={CARBON_TREND_DATA} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="quarter" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000)?.toFixed(0)}k`} />
            <Tooltip content={<CarbonTooltip />} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
            <Line type="monotone" dataKey="actual" name="Actual CO₂ Avoided" stroke={COLORS?.actual} strokeWidth={2.5} dot={{ r: 3, fill: COLORS?.actual }} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey="target" name="Target Trajectory" stroke={COLORS?.target} strokeWidth={2} strokeDasharray="5 4" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Social Impact Trend */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <h3 className="font-heading font-semibold text-foreground mb-1" style={{ fontSize: '0.9375rem' }}>Social Impact Trend</h3>
        <p className="font-caption text-muted-foreground text-xs mb-4">Jobs supported (FTE) and community spend (£m)</p>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={SOCIAL_TREND_DATA} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="quarter" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={(v) => `£${v}m`} />
            <Tooltip content={<SocialTooltip />} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
            <Bar yAxisId="left" dataKey="jobs" name="Jobs Supported" fill={COLORS?.jobs} radius={[3, 3, 0, 0]} />
            <Bar yAxisId="right" dataKey="communitySpend" name="Community Spend" fill={COLORS?.community} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
